import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Search, Map, ChevronDown, X } from 'lucide-react';
import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { getAddressSuggestions } from '../../../lib/services/address-property-search';

interface SearchBarProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  isSearching: boolean;
  onSearch: (e: React.FormEvent) => void;
  onSearchInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  
  commuteAddressRent?: string;
  commuteDriveTimeRent?: number | null;
  onSuggestionSelect?: (suggestion: any) => void;
  customToggleButton?: React.ReactNode;
}

export function SearchBar({
  searchQuery,
  setSearchQuery,
  isSearching,
  onSearch,
  onSearchInputChange,
  
  commuteAddressRent,
  commuteDriveTimeRent,
  onSuggestionSelect,
  customToggleButton,
}: SearchBarProps) {
  const [, setLocation] = useLocation();
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [inputValue, setInputValue] = useState(searchQuery);
  const [searchInitiated, setSearchInitiated] = useState(false);
  const [suggestionSelected, setSuggestionSelected] = useState(false); // New state to track if suggestion was selected
  const [isInputFocused, setIsInputFocused] = useState(false); // Track if input is focused
  const [userHasTyped, setUserHasTyped] = useState(false); // Track if user has actively typed
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const prevSearchQueryRef = useRef(searchQuery); // Track previous searchQuery value
  const [debouncedInput, setDebouncedInput] = useState(inputValue);

  // Removed cycling blue animation

  // Update input value when searchQuery changes (e.g., from suggestion selection)
  useEffect(() => {
    setInputValue(searchQuery);
    // Reset user typing state when searchQuery changes externally (e.g., from navigation)
    // Only reset if the searchQuery actually changed from a previous value
    if (searchQuery !== prevSearchQueryRef.current) {
      setUserHasTyped(false);
      prevSearchQueryRef.current = searchQuery;
    }
  }, [searchQuery]);

  // Reset searchInitiated when search completes, with a small delay for visibility
  useEffect(() => {
    
    if (!isSearching) {
      // Search completed, setting timer to reset searchInitiated in 800ms
      const timer = setTimeout(() => {
        setSearchInitiated(false);
        // Timer expired, setting searchInitiated=false
      }, 800);
      
      return () => {
        clearTimeout(timer);
        // Timer cleared
      };
    }
  }, [isSearching, searchInitiated]);

  // Fallback: Reset searchInitiated after a reasonable time if isSearching never becomes true
  useEffect(() => {
    if (searchInitiated && !isSearching) {
      // Fallback timer: searchInitiated=true but isSearching=false, will reset in 3 seconds
      const fallbackTimer = setTimeout(() => {
        setSearchInitiated(false);
        // Fallback timer expired, setting searchInitiated=false
      }, 3000);
      
      return () => {
        clearTimeout(fallbackTimer);
        // Fallback timer cleared
      };
    }
  }, [searchInitiated, isSearching]);

  // Debounce input to limit API calls
  useEffect(() => {
    const t = setTimeout(() => setDebouncedInput(inputValue), 400);
    return () => clearTimeout(t);
  }, [inputValue]);

  useEffect(() => {
    const queryEnabled = debouncedInput.length >= 3 && !suggestionSelected && !isSearching;
  }, [debouncedInput, suggestionSelected, isSearching]);

  // Fetch address suggestions when input value changes (not searchQuery)
  const { data: suggestions = [] as any[], isLoading: loadingSuggestions } = useQuery({
    queryKey: ['address-suggestions', debouncedInput],
    queryFn: async () => {
      
      if (!debouncedInput || debouncedInput.length < 3) {
        return [] as any[];
      }
      
      const raw = await getAddressSuggestions(debouncedInput);
      
      // Map to UI-friendly shape expected by the dropdown
      const mapped = raw.map((item: any, index: number) => {
        const meta = item.metaData || {};
        const street = [meta.streetNumber, meta.streetName].filter(Boolean).join(' ').trim();
        const city = meta.city || '';
        const state = meta.state || '';
        const zip = meta.zipCode || '';
        const fullAddress = item.display || [street, city, state, zip].filter(Boolean).join(', ');
        return {
          id: `${fullAddress}-${index}`,
          address: street || fullAddress,
          city,
          state,
          zipCode: zip,
          fullAddress,
          metaData: meta,
        };
      });
      
      return mapped;
    },
    enabled: debouncedInput.length >= 3 && isInputFocused && userHasTyped,
    staleTime: 30000,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
    },
    onError: (err) => {
    }
  });

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion: any) => {
    
    const value = suggestion.fullAddress || suggestion.address;
    
    setInputValue(value);
    setSearchQuery(value); // Update the actual search query
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
    setSuggestionSelected(true); // Mark that a suggestion was selected
    setUserHasTyped(false); // Reset user typing state so they can type again
    
    
    onSuggestionSelect?.(suggestion);
    // Don't automatically trigger search - let user click search button
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedSuggestionIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < suggestions.length) {
          handleSuggestionSelect(suggestions[selectedSuggestionIndex]);
        } else {
          // If no suggestion is selected, just hide suggestions but don't search
          setShowSuggestions(false);
          setSelectedSuggestionIndex(-1);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        break;
    }
  };

  // Handle input change - only update the input value
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    
    setInputValue(newValue);
    setUserHasTyped(true); // Mark that user has actively typed
    // Reset suggestion selected state when user starts typing after a selection
    if (suggestionSelected) {
      setSuggestionSelected(false);
    }
    // Call onSearchInputChange to handle commute filter clearing, etc.
    onSearchInputChange(e);
    setSelectedSuggestionIndex(-1);
  };

  // Handle input focus
  const handleInputFocus = () => {
    setIsInputFocused(true);
    // Only show suggestions if user has typed and we have suggestions
    if (suggestions.length > 0 && inputValue.length >= 3 && userHasTyped) {
      setShowSuggestions(true);
    }
  };

  // Handle input blur
  const handleInputBlur = () => {
    // Add a small delay to allow mousedown events on suggestions to fire first
    setTimeout(() => {
      setIsInputFocused(false);
    }, 100);
    // Don't immediately hide suggestions on blur to allow clicking on them
    // The click outside handler will take care of hiding them
  };

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-show suggestions when they load
  useEffect(() => {
    
    // Don't show suggestions if suggestion was selected and user hasn't typed since
    if (suggestionSelected && !userHasTyped) {
      setShowSuggestions(false);
      return;
    }
    
    // Only show suggestions if input is focused, user has typed, and we have valid input
    if (suggestions.length > 0 && inputValue.length >= 3 && isInputFocused && userHasTyped) {
      setShowSuggestions(true);
    } else if (suggestions.length === 0 && inputValue.length >= 3) {
      setShowSuggestions(false);
    } else if (inputValue.length < 3) {
      setShowSuggestions(false);
    } else if (!isInputFocused || !userHasTyped) {
      setShowSuggestions(false);
    }
  }, [suggestions, inputValue, suggestionSelected, isSearching, isInputFocused, userHasTyped]);

  // Handle search form submission
  const handleSearchSubmit = (e: React.FormEvent) => {
    
    e.preventDefault();
    // Search button clicked, setting searchInitiated=true
    setSearchInitiated(true);
    // Reset suggestion selected state since we're performing a search
    setSuggestionSelected(false);
    // Update the actual search query with the current input value
    setSearchQuery(inputValue);
    // Hide suggestions when search is executed
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
    
    
    // Execute the search
    onSearch(e);
    
    // The hook will handle stopping the animation after minimum duration
  };

  return (
    <div className="mb-0 flex gap-2 items-center relative">
      <form onSubmit={handleSearchSubmit} className="flex flex-1 gap-2">
        <div className="relative flex-1">
          <Input
            ref={inputRef}
            type="text"
            placeholder="Enter city, state, address, or ZIP"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            className={`pl-4 pr-12 rounded-full ${
              commuteAddressRent && commuteDriveTimeRent !== null ? 'border-blue-300 bg-blue-50' : ''
            }`}
          />
          
        </div>
        <Button 
          type="submit" 
          disabled={isSearching} 
          className={`rounded-full px-5 h-12 w-12 flex items-center justify-center transition-all duration-200 bg-blue-600 hover:bg-blue-700 shadow-xl transform hover:scale-105 active:scale-95`} 
          aria-label="Search"
          title="Search status indicator"
        >
          <Search className="h-6 w-6 text-white" />

        </Button>
      </form>
      {customToggleButton || (
        <button
          type="button"
          className="ml-2 flex items-center justify-center rounded-full bg-white shadow border border-gray-200 w-12 h-12 hover:bg-blue-50 transition"
          aria-label="Map View"
          onClick={() => {
            try {
              sessionStorage.removeItem('map:focus-active');
              sessionStorage.removeItem('map:disable-auto-fit');
              sessionStorage.removeItem('map:center');
              sessionStorage.removeItem('map:zoom');
            } catch {}
            setLocation('/map');
          }}
        >
          <Map className="h-6 w-6 text-blue-600" />
        </button>
      )}

      {/* Address Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (() => {
        return (
        <div
          ref={suggestionsRef}
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto"
          style={{ width: 'calc(100% - 3rem)' }}
        >
          <div className="p-2 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Suggestions</span>
              <button
                type="button"
                onClick={() => setShowSuggestions(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="py-1">
            {suggestions.map((suggestion, index) => (
              <button
                key={suggestion.id || suggestion.name || suggestion.displayName || suggestion.text || index}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent input blur
                  handleSuggestionSelect(suggestion);
                }}
                className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                  index === selectedSuggestionIndex ? 'bg-blue-50 border-l-2 border-blue-500' : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{suggestion.address}</div>
                    <div className="text-sm text-gray-500">
                      {suggestion.city}, {suggestion.state} {suggestion.zipCode}
                    </div>
                  </div>
                  <ChevronDown className="h-4 w-4 text-gray-400 ml-2" />
                </div>
              </button>
            ))}
          </div>
        </div>
        );
      })()}

      {/* Loading indicator for suggestions */}
      {loadingSuggestions && inputValue.length >= 3 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4" style={{ width: 'calc(100% - 3rem)' }}>
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
            <span className="ml-2 text-sm text-gray-500">Loading suggestions...</span>
          </div>
        </div>
      )}
    </div>
  );
} 